import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import {
  getNutrientCrops,
  getNutrientVarietiesByCrop,
  getVarietyNutrientRequirements,
  saveVarietyNutrientRequirements,
  exportNutrientsExcel,
  importNutrientsExcel,
} from "../lib/api";

// Standard 11 nutrients matching backend schema
export const NUTRIENT_COLUMNS = [
  { key: "nitrogen_n_kg_ha", label: "Nitrogen (N)", shortLabel: "N", unit: "kg/ha" },
  { key: "phosphorus_p2o5_kg_ha", label: "Phosphorus (P₂O₅)", shortLabel: "P₂O₅", unit: "kg/ha" },
  { key: "potassium_k2o_kg_ha", label: "Potassium (K₂O)", shortLabel: "K₂O", unit: "kg/ha" },
  { key: "sulfur_s_kg_ha", label: "Sulfur (S)", shortLabel: "S", unit: "kg/ha" },
  { key: "calcium_ca_kg_ha", label: "Calcium (Ca)", shortLabel: "Ca", unit: "kg/ha" },
  { key: "magnesium_mg_kg_ha", label: "Magnesium (Mg)", shortLabel: "Mg", unit: "kg/ha" },
  { key: "zinc_zn_kg_ha", label: "Zinc (Zn)", shortLabel: "Zn", unit: "kg/ha" },
  { key: "boron_b_kg_ha", label: "Boron (B)", shortLabel: "B", unit: "kg/ha" },
  { key: "iron_fe_kg_ha", label: "Iron (Fe)", shortLabel: "Fe", unit: "kg/ha" },
  { key: "manganese_mn_kg_ha", label: "Manganese (Mn)", shortLabel: "Mn", unit: "kg/ha" },
  { key: "copper_cu_kg_ha", label: "Copper (Cu)", shortLabel: "Cu", unit: "kg/ha" },
];

const PhaseWiseNutritiveValue = () => {
  const getToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("screen");

  // Edit-mode indicator derived from query params
  const editCropId = searchParams.get("cropId");
  const editVarietyId = searchParams.get("varietyId");
  const isEditMode = Boolean(editCropId && editVarietyId);

  // Track whether bootstrap deep-link has already run
  const bootstrapDoneRef = useRef(false);

  // ────────────────────────────────────────────────────────────────────────
  // CROPS
  // ────────────────────────────────────────────────────────────────────────
  const [crops, setCrops] = useState([]);
  const [loadingCrops, setLoadingCrops] = useState(false);

  const fetchCropsList = useCallback(async () => {
    try {
      setLoadingCrops(true);
      const token = getToken();
      const res = await getNutrientCrops(token);
      if (res && res.data) {
        const sorted = [...res.data].sort((a, b) =>
          (a.CropName || "").localeCompare(b.CropName || "")
        );
        setCrops(sorted);
      }
    } catch (err) {
      console.error("Error fetching crops:", err);
      toast.error(err?.response?.data?.message || "Failed to load crops list.");
    } finally {
      setLoadingCrops(false);
    }
  }, []);

  useEffect(() => {
    fetchCropsList();
  }, [fetchCropsList]);

  // ────────────────────────────────────────────────────────────────────────
  // SCREEN CONTROLS
  // ────────────────────────────────────────────────────────────────────────
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [cropDropdownOpen, setCropDropdownOpen] = useState(false);
  const [cropSearchTerm, setCropSearchTerm] = useState("");

  const [varieties, setVarieties] = useState([]);
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [varietyDropdownOpen, setVarietyDropdownOpen] = useState(false);
  const [varietySearchTerm, setVarietySearchTerm] = useState("");

  const [matrixPhases, setMatrixPhases] = useState([]);
  const [matrixValues, setMatrixValues] = useState({});
  const [initialMatrixValues, setInitialMatrixValues] = useState({});
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [hasExistingSavedData, setHasExistingSavedData] = useState(false);

  const cropRef = useRef(null);
  const varietyRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cropRef.current && !cropRef.current.contains(event.target))
        setCropDropdownOpen(false);
      if (varietyRef.current && !varietyRef.current.contains(event.target))
        setVarietyDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCrops = useMemo(() => {
    if (!cropSearchTerm.trim()) return crops;
    const q = cropSearchTerm.toLowerCase();
    return crops.filter(
      (c) =>
        (c.CropName && c.CropName.toLowerCase().includes(q)) ||
        (c.CropShortName && c.CropShortName.toLowerCase().includes(q))
    );
  }, [crops, cropSearchTerm]);

  const handleSelectCrop = async (crop) => {
    setSelectedCrop(crop);
    setCropDropdownOpen(false);
    setCropSearchTerm("");

    setSelectedVariety(null);
    setVarieties([]);
    setMatrixPhases([]);
    setMatrixValues({});
    setInitialMatrixValues({});
    setHasExistingSavedData(false);

    if (!crop) return;

    try {
      setLoadingVarieties(true);
      const token = getToken();
      const res = await getNutrientVarietiesByCrop(crop.ID, token);
      if (res && res.data) {
        const sorted = [...res.data].sort((a, b) =>
          (a.VariantName || "").localeCompare(b.VariantName || "")
        );
        setVarieties(sorted);
      }
    } catch (err) {
      console.error("Error fetching varieties:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load varieties for selected crop."
      );
    } finally {
      setLoadingVarieties(false);
    }
  };

  const filteredVarieties = useMemo(() => {
    if (!varietySearchTerm.trim()) return varieties;
    const q = varietySearchTerm.toLowerCase();
    return varieties.filter(
      (v) => v.VariantName && v.VariantName.toLowerCase().includes(q)
    );
  }, [varieties, varietySearchTerm]);

  const handleSelectVariety = async (variety) => {
    setSelectedVariety(variety);
    setVarietyDropdownOpen(false);
    setVarietySearchTerm("");

    if (!variety) {
      setMatrixPhases([]);
      setMatrixValues({});
      setInitialMatrixValues({});
      setHasExistingSavedData(false);
      return;
    }

    try {
      setMatrixLoading(true);
      const token = getToken();
      const res = await getVarietyNutrientRequirements(variety.ID, token);

      if (res && res.data) {
        const phasesData = res.data.phases || [];
        const existingData = res.data.existing_requirements || [];

        setMatrixPhases(phasesData);
        setHasExistingSavedData(existingData.length > 0);

        const existingMap = new Map();
        for (const req of existingData)
          existingMap.set(String(req.phase_id), req);

        const valuesObj = {};
        for (const nut of NUTRIENT_COLUMNS) {
          valuesObj[nut.key] = {};
          for (const ph of phasesData) {
            const pId = String(ph.PhaseID || ph.id);
            const req = existingMap.get(pId);
            const val =
              req && req[nut.key] !== null && req[nut.key] !== undefined
                ? String(req[nut.key])
                : "";
            valuesObj[nut.key][pId] = val;
          }
        }

        setMatrixValues(valuesObj);
        setInitialMatrixValues(JSON.parse(JSON.stringify(valuesObj)));
      }
    } catch (err) {
      console.error("Error loading variety nutrient requirements:", err);
      toast.error(
        err?.response?.data?.message || "Failed to fetch nutrient matrix for variety."
      );
    } finally {
      setMatrixLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // DEEP-LINK BOOTSTRAP: pre-select crop & variety from ?cropId&varietyId
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (bootstrapDoneRef.current) return;
    if (!editCropId || !editVarietyId) return;
    if (loadingCrops) return;
    if (!crops.length) return;

    const crop = crops.find((c) => String(c.ID) === String(editCropId));
    if (!crop) {
      console.warn("Edit mode: crop not found for id", editCropId);
      bootstrapDoneRef.current = true;
      return;
    }

    const runBootstrap = async () => {
      try {
        bootstrapDoneRef.current = true;

        // 1. Select crop (loads varieties into state)
        await handleSelectCrop(crop);

        // 2. Fetch varieties directly to guarantee we have them now
        const token = getToken();
        const res = await getNutrientVarietiesByCrop(crop.ID, token);
        const sorted = [...(res?.data || [])].sort((a, b) =>
          (a.VariantName || "").localeCompare(b.VariantName || "")
        );
        setVarieties(sorted);

        const variety = sorted.find(
          (v) => String(v.ID) === String(editVarietyId)
        );
        if (!variety) {
          console.warn("Edit mode: variety not found for id", editVarietyId);
          toast.warning("Selected variety not found for this crop.");
          return;
        }

        // 3. Select variety (loads nutrient matrix)
        await handleSelectVariety(variety);
      } catch (err) {
        console.error("Bootstrap error:", err);
      }
    };

    runBootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCropId, editVarietyId, crops, loadingCrops]);

  const handleCellChange = (nutrientKey, phaseId, rawVal) => {
    if (rawVal !== "" && !/^\d*\.?\d*$/.test(rawVal)) return;
    setMatrixValues((prev) => ({
      ...prev,
      [nutrientKey]: { ...(prev[nutrientKey] || {}), [phaseId]: rawVal },
    }));
  };

  const handleFillZeroEmpty = () => {
    setMatrixValues((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      for (const nut of NUTRIENT_COLUMNS) {
        if (!next[nut.key]) next[nut.key] = {};
        for (const ph of matrixPhases) {
          const pId = String(ph.PhaseID || ph.id);
          if (
            next[nut.key][pId] === "" ||
            next[nut.key][pId] === undefined ||
            next[nut.key][pId] === null
          ) {
            next[nut.key][pId] = "0";
          }
        }
      }
      return next;
    });
    toast.info("Empty cells populated with 0.");
  };

  const handleClearAll = () => {
    setMatrixValues((prev) => {
      const next = {};
      for (const nut of NUTRIENT_COLUMNS) {
        next[nut.key] = {};
        for (const ph of matrixPhases) {
          next[nut.key][String(ph.PhaseID || ph.id)] = "";
        }
      }
      return next;
    });
  };

  const handleResetToSaved = () => {
    setMatrixValues(JSON.parse(JSON.stringify(initialMatrixValues)));
    toast.info("Reset to server saved values.");
  };

  const handleSaveMatrix = async () => {
    if (!selectedCrop || !selectedVariety) {
      toast.warning("Please select both a crop and a variety.");
      return;
    }
    if (!matrixPhases || matrixPhases.length === 0) {
      toast.warning("No phases available for this variety to save.");
      return;
    }

    try {
      setSavingMatrix(true);
      const phase_nutrients = matrixPhases.map((ph) => {
        const pId = String(ph.PhaseID || ph.id);
        const item = { phase_id: pId };
        for (const nut of NUTRIENT_COLUMNS) {
          const raw = matrixValues[nut.key]?.[pId];
          if (
            raw !== "" &&
            raw !== null &&
            raw !== undefined &&
            !isNaN(Number(raw))
          ) {
            item[nut.key] = Number(raw);
          } else {
            item[nut.key] = null;
          }
        }
        return item;
      });

      const payload = {
        crop_id: selectedCrop.ID,
        variety_id: selectedVariety.ID,
        phase_nutrients,
      };

      const token = getToken();
      const res = await saveVarietyNutrientRequirements(payload, token);

      toast.success(
        res?.message || "Phase-wise nutritive values saved successfully!"
      );
      setHasExistingSavedData(true);
      setInitialMatrixValues(JSON.parse(JSON.stringify(matrixValues)));

      // Clear edit-mode query params after successful save
      if (isEditMode) {
        navigate("/phase-wise-nutritive-value", { replace: true });
      }
    } catch (err) {
      console.error("Error saving nutrient requirements:", err);
      toast.error(
        err?.response?.data?.message || "Failed to save nutrient requirements."
      );
    } finally {
      setSavingMatrix(false);
    }
  };

  const calculateNutrientTotal = (nutrientKey) => {
    let total = 0;
    let hasValue = false;
    for (const ph of matrixPhases) {
      const pId = String(ph.PhaseID || ph.id);
      const val = matrixValues[nutrientKey]?.[pId];
      if (
        val !== "" &&
        val !== null &&
        val !== undefined &&
        !isNaN(Number(val))
      ) {
        total += Number(val);
        hasValue = true;
      }
    }
    return hasValue ? Math.round(total * 100) / 100 : "-";
  };

  // ────────────────────────────────────────────────────────────────────────
  // EXCEL IMPORT / EXPORT
  // ────────────────────────────────────────────────────────────────────────
  const [exportSelectedCrops, setExportSelectedCrops] = useState([]);
  const [exportCropSearch, setExportCropSearch] = useState("");
  const [exportVarietiesMap, setExportVarietiesMap] = useState({});
  const [exportSelectedVarieties, setExportSelectedVarieties] = useState([]);
  const [exportVarietySearch, setExportVarietySearch] = useState("");
  const [loadingExportVarieties, setLoadingExportVarieties] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccessInfo, setImportSuccessInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleToggleExportCrop = async (cropId) => {
    let nextSelected;
    if (exportSelectedCrops.includes(cropId)) {
      nextSelected = exportSelectedCrops.filter((id) => id !== cropId);
      setExportSelectedCrops(nextSelected);
      const remainingCropsVarieties = [];
      for (const [cId, vList] of Object.entries(exportVarietiesMap)) {
        if (cId !== cropId)
          remainingCropsVarieties.push(...vList.map((v) => v.ID));
      }
      setExportSelectedVarieties((prev) =>
        prev.filter((vId) => remainingCropsVarieties.includes(vId))
      );
    } else {
      nextSelected = [...exportSelectedCrops, cropId];
      setExportSelectedCrops(nextSelected);

      if (!exportVarietiesMap[cropId]) {
        try {
          setLoadingExportVarieties(true);
          const token = getToken();
          const res = await getNutrientVarietiesByCrop(cropId, token);
          if (res && res.data) {
            setExportVarietiesMap((prev) => ({ ...prev, [cropId]: res.data }));
            setExportSelectedVarieties((prev) => [
              ...prev,
              ...res.data.map((v) => v.ID),
            ]);
          }
        } catch (err) {
          console.error("Error loading varieties for crop in export:", err);
        } finally {
          setLoadingExportVarieties(false);
        }
      } else {
        const toAdd = (exportVarietiesMap[cropId] || []).map((v) => v.ID);
        setExportSelectedVarieties((prev) => [
          ...new Set([...prev, ...toAdd]),
        ]);
      }
    }
  };

  const handleSelectAllExportCrops = async () => {
    if (exportSelectedCrops.length === crops.length) {
      setExportSelectedCrops([]);
      setExportSelectedVarieties([]);
      return;
    }
    const allCropIds = crops.map((c) => c.ID);
    setExportSelectedCrops(allCropIds);

    try {
      setLoadingExportVarieties(true);
      const token = getToken();
      const newMap = { ...exportVarietiesMap };
      const allVarIds = [];

      await Promise.all(
        allCropIds.map(async (cId) => {
          if (!newMap[cId]) {
            try {
              const res = await getNutrientVarietiesByCrop(cId, token);
              if (res && res.data) newMap[cId] = res.data;
            } catch (e) {}
          }
          if (newMap[cId]) allVarIds.push(...newMap[cId].map((v) => v.ID));
        })
      );

      setExportVarietiesMap(newMap);
      setExportSelectedVarieties(allVarIds);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingExportVarieties(false);
    }
  };

  const handleToggleExportVariety = (vId) => {
    if (exportSelectedVarieties.includes(vId)) {
      setExportSelectedVarieties(
        exportSelectedVarieties.filter((id) => id !== vId)
      );
    } else {
      setExportSelectedVarieties([...exportSelectedVarieties, vId]);
    }
  };

  const allAvailableExportVarieties = useMemo(() => {
    const list = [];
    for (const cId of exportSelectedCrops) {
      if (exportVarietiesMap[cId]) list.push(...exportVarietiesMap[cId]);
    }
    return list;
  }, [exportSelectedCrops, exportVarietiesMap]);

  const handleSelectAllExportVarieties = () => {
    if (exportSelectedVarieties.length === allAvailableExportVarieties.length) {
      setExportSelectedVarieties([]);
    } else {
      setExportSelectedVarieties(allAvailableExportVarieties.map((v) => v.ID));
    }
  };

  const handleExportExcel = async () => {
    if (exportSelectedCrops.length === 0) {
      toast.warning("Please select at least one crop to export.");
      return;
    }
    if (exportSelectedVarieties.length === 0) {
      toast.warning("Please select at least one variety to export.");
      return;
    }

    try {
      setIsExporting(true);
      const token = getToken();
      const payload = {
        crop_ids: exportSelectedCrops,
        variety_ids: exportSelectedVarieties,
      };
      const res = await exportNutrientsExcel(payload, token);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `Crop_Variety_Phase_Nutrient_Requirements_${Date.now()}.xlsx`;
      saveAs(blob, fileName);
      toast.success("Excel file generated and download started!");
    } catch (err) {
      console.error("Error exporting excel:", err);
      toast.error(
        err?.response?.data?.message || "Failed to export Excel file."
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportErrors([]);
      setImportSuccessInfo(null);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportFile(e.dataTransfer.files[0]);
      setImportErrors([]);
      setImportSuccessInfo(null);
    }
  };

  const handleRemoveImportFile = () => {
    setImportFile(null);
    setImportErrors([]);
    setImportSuccessInfo(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.warning("Please choose an Excel file to import.");
      return;
    }

    try {
      setIsImporting(true);
      setImportErrors([]);
      setImportSuccessInfo(null);

      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("excelFile", importFile);

      const token = getToken();
      const res = await importNutrientsExcel(formData, token);

      toast.success(
        res?.message || "Nutrient requirements successfully imported!"
      );
      setImportSuccessInfo(res?.data || { message: res?.message });
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";

      if (selectedVariety) handleSelectVariety(selectedVariety);
    } catch (err) {
      console.error("Error importing Excel:", err);
      const errRes = err?.response?.data;
      if (errRes?.errors && Array.isArray(errRes.errors)) {
        setImportErrors(errRes.errors);
        toast.error(
          `Import failed with ${errRes.errors.length} validation errors. Please review below.`
        );
      } else {
        toast.error(
          errRes?.message || err.message || "Failed to import Excel file."
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="card p-0 radius-12">
      {/* ─── Scoped CSS to tighten table cells ──────────────────────── */}
      <style>{`
        .nutrient-matrix-table > :not(caption) > * > * {
          padding-left: 4px !important;
          padding-right: 4px !important;
        }
        .nutrient-matrix-table thead th {
          padding-top: 6px !important;
          padding-bottom: 6px !important;
          font-size: 12px;
        }
        .nutrient-matrix-table tbody td {
          padding-top: 4px !important;
          padding-bottom: 4px !important;
        }
      `}</style>

      {/* ─── Header ─────────────────────────────────────────────────── */}
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Mode Switcher */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className={`btn btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2 text-sm ${
                activeTab === "screen" ? "btn-yellow" : "btn-light"
              }`}
              onClick={() => setActiveTab("screen")}
            >
              <Icon icon="mdi:table-edit" style={{ fontSize: 16 }} />
              <span className="text-nowrap">Screen Controls</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm px-16 py-10 radius-8 d-flex align-items-center gap-2 text-sm ${
                activeTab === "excel" ? "btn-yellow" : "btn-light"
              }`}
              onClick={() => setActiveTab("excel")}
            >
              <Icon icon="mdi:file-excel-box" style={{ fontSize: 16 }} />
              <span className="text-nowrap">Excel Import / Export</span>
            </button>
          </div>

          {/* Edit mode banner + back button */}
          {isEditMode && (
            <div className="d-flex align-items-center gap-2">
              <span className="px-12 py-6 radius-4 text-xs fw-medium bg-warning-focus text-warning-600 border border-warning-main d-inline-flex align-items-center gap-1">
                <Icon icon="lucide:pencil" style={{ fontSize: 12 }} />
                Edit Mode
              </span>
              <button
                type="button"
                className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs"
                onClick={() => navigate("/crop-nutrient")}
              >
                <Icon icon="lucide:arrow-left" style={{ fontSize: 14 }} />
                Back to List
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── Body ──────────────────────────────────────────────────── */}
      <div className="p-24">
        {/* ================================================================ */}
        {/* SCREEN CONTROLS */}
        {/* ================================================================ */}
        {activeTab === "screen" && (
          <>
            {/* Selectors row */}
            <div className="row g-3 mb-20">
              {/* Crop */}
              <div className="col-md-6 col-lg-5">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2">
                  Crop <span className="text-danger">*</span>
                </label>
                <div className="position-relative" ref={cropRef}>
                  <button
                    type="button"
                    className="form-select text-start d-flex align-items-center justify-content-between"
                    onClick={() => setCropDropdownOpen(!cropDropdownOpen)}
                    disabled={loadingCrops}
                    style={{ minHeight: 40 }}
                  >
                    <span
                      className={`text-truncate ${
                        selectedCrop
                          ? "text-primary-light fw-medium"
                          : "text-secondary-light"
                      }`}
                    >
                      {selectedCrop ? selectedCrop.CropName : "Select Crop..."}
                    </span>
                  </button>

                  {cropDropdownOpen && (
                    <div
                      className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden"
                      style={{ zIndex: 30 }}
                    >
                      <div className="p-8 border-bottom">
                        <div className="navbar-search">
                          <input
                            className="bg-base h-36-px ps-30 w-100"
                            placeholder="Search crop..."
                            value={cropSearchTerm}
                            onChange={(e) => setCropSearchTerm(e.target.value)}
                            autoFocus
                          />
                          <Icon icon="ion:search-outline" className="icon" />
                        </div>
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: 240 }}>
                        {loadingCrops ? (
                          <div className="p-16 text-center text-secondary-light text-sm">
                            Loading crops...
                          </div>
                        ) : filteredCrops.length === 0 ? (
                          <div className="p-16 text-center text-secondary-light text-sm">
                            No crops found
                          </div>
                        ) : (
                          filteredCrops.map((c) => (
                            <div
                              key={c.ID}
                              className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${
                                selectedCrop?.ID === c.ID
                                  ? "bg-primary-focus text-primary-600 fw-medium"
                                  : "text-secondary-light"
                              }`}
                              onClick={() => handleSelectCrop(c)}
                            >
                              <span className="text-sm">{c.CropName}</span>
                              {selectedCrop?.ID === c.ID && (
                                <Icon
                                  icon="lucide:check"
                                  style={{ fontSize: 14 }}
                                />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Variety */}
              <div className="col-md-6 col-lg-5">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2">
                  Variety <span className="text-danger">*</span>
                </label>
                <div className="position-relative" ref={varietyRef}>
                  <button
                    type="button"
                    className="form-select text-start d-flex align-items-center justify-content-between"
                    onClick={() => setVarietyDropdownOpen(!varietyDropdownOpen)}
                    disabled={!selectedCrop || loadingVarieties}
                    style={{ minHeight: 40 }}
                  >
                    <span
                      className={`text-truncate ${
                        selectedVariety
                          ? "text-primary-light fw-medium"
                          : "text-secondary-light"
                      }`}
                    >
                      {!selectedCrop
                        ? "Select a crop first..."
                        : loadingVarieties
                        ? "Loading varieties..."
                        : selectedVariety
                        ? selectedVariety.VariantName
                        : "Select Variety..."}
                    </span>
                  </button>

                  {varietyDropdownOpen && selectedCrop && (
                    <div
                      className="position-absolute start-0 end-0 mt-1 bg-base border radius-8 shadow-lg overflow-hidden"
                      style={{ zIndex: 30 }}
                    >
                      <div className="p-8 border-bottom">
                        <div className="navbar-search">
                          <input
                            className="bg-base h-36-px ps-30 w-100"
                            placeholder="Search variety..."
                            value={varietySearchTerm}
                            onChange={(e) =>
                              setVarietySearchTerm(e.target.value)
                            }
                            autoFocus
                          />
                          <Icon icon="ion:search-outline" className="icon" />
                        </div>
                      </div>
                      <div className="overflow-auto" style={{ maxHeight: 240 }}>
                        {loadingVarieties ? (
                          <div className="p-16 text-center text-secondary-light text-sm">
                            Loading varieties...
                          </div>
                        ) : filteredVarieties.length === 0 ? (
                          <div className="p-16 text-center text-secondary-light text-sm">
                            {varieties.length === 0
                              ? "No varieties found for this crop"
                              : "No matching varieties"}
                          </div>
                        ) : (
                          filteredVarieties.map((v) => (
                            <div
                              key={v.ID}
                              className={`d-flex align-items-center justify-content-between px-16 py-8 cursor-pointer hover-bg-light ${
                                selectedVariety?.ID === v.ID
                                  ? "bg-success-focus text-success-600 fw-medium"
                                  : "text-secondary-light"
                              }`}
                              onClick={() => handleSelectVariety(v)}
                            >
                              <span className="text-sm">{v.VariantName}</span>
                              {selectedVariety?.ID === v.ID && (
                                <Icon
                                  icon="lucide:check"
                                  style={{ fontSize: 14 }}
                                />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Phase count */}
              <div className="col-md-12 col-lg-2">
                <label className="form-label text-xs fw-semibold text-secondary-light mb-2 d-block">
                  &nbsp;
                </label>
                {selectedVariety ? (
                  <div
                    className="px-12 py-8 bg-light radius-8 text-center"
                    style={{ minHeight: 40 }}
                  >
                    <div className="text-xs text-secondary-light">Phases</div>
                    <div className="fw-semibold text-primary-light text-sm">
                      {matrixPhases.length}
                    </div>
                  </div>
                ) : (
                  <div
                    className="px-12 py-8 bg-light radius-8 text-center"
                    style={{ minHeight: 40 }}
                  >
                    <div className="text-xs text-secondary-light">—</div>
                  </div>
                )}
              </div>
            </div>

            {/* Matrix / Empty states */}
            {!selectedCrop ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon
                  icon="mdi:barley"
                  style={{ fontSize: 40 }}
                  className="text-secondary-light mb-12"
                />
                <h6 className="fw-semibold text-primary-light mb-2">
                  Select a Crop to begin
                </h6>
                <p className="text-sm text-secondary-light mb-0">
                  Pick a crop from the alphabetical dropdown above.
                </p>
              </div>
            ) : !selectedVariety ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon
                  icon="mdi:seed"
                  style={{ fontSize: 40 }}
                  className="text-secondary-light mb-12"
                />
                <h6 className="fw-semibold text-primary-light mb-2">
                  Now select a Variety for "{selectedCrop.CropName}"
                </h6>
                <p className="text-sm text-secondary-light mb-0">
                  The nutrient matrix will appear with all configured phases as
                  columns.
                </p>
              </div>
            ) : matrixLoading ? (
              <div className="text-center py-48">
                <span
                  className="spinner-border text-primary spinner-border-sm me-2"
                  role="status"
                />
                <span className="text-secondary-light">
                  Loading phase-wise nutrient requirements...
                </span>
              </div>
            ) : matrixPhases.length === 0 ? (
              <div className="text-center py-48 bg-light radius-12">
                <Icon
                  icon="lucide:alert-triangle"
                  style={{ fontSize: 36 }}
                  className="text-warning-600 mb-12"
                />
                <h6 className="fw-semibold text-primary-light mb-2">
                  No Growth Phases Configured
                </h6>
                <p className="text-sm text-secondary-light mb-0">
                  This variety does not have any active growth phases assigned
                  in Master Phases.
                </p>
              </div>
            ) : (
              <>
                {/* Action bar */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-16 p-12 bg-light radius-8">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="px-12 py-4 radius-4 fw-medium text-xs bg-primary-focus text-primary-600 border border-primary-main">
                      {selectedCrop.CropName} · {selectedVariety.VariantName}
                    </span>
                    {hasExistingSavedData ? (
                      <span className="px-12 py-4 radius-4 fw-medium text-xs bg-success-focus text-success-600 border border-success-main d-inline-flex align-items-center gap-1">
                        <Icon icon="lucide:check" style={{ fontSize: 12 }} />
                        Existing values loaded
                      </span>
                    ) : (
                      <span className="px-12 py-4 radius-4 fw-medium text-xs bg-warning-focus text-warning-600 border border-warning-main">
                        New entry
                      </span>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs"
                      onClick={handleFillZeroEmpty}
                    >
                      <Icon icon="lucide:plus" style={{ fontSize: 14 }} />
                      Fill Zero
                    </button>
                    <button
                      type="button"
                      className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs"
                      onClick={handleResetToSaved}
                    >
                      <Icon icon="lucide:rotate-ccw" style={{ fontSize: 14 }} />
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-light btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-1 text-xs text-danger-600"
                      onClick={handleClearAll}
                    >
                      <Icon icon="lucide:x" style={{ fontSize: 14 }} />
                      Clear
                    </button>
                  </div>
                </div>

                {/* Matrix table */}
                <div
                  className="table-responsive radius-8 border mb-20"
                  style={{ maxHeight: 600 }}
                >
                  <table
                    className="table bordered-table sm-table mb-0 nutrient-matrix-table"
                    style={{
                      minWidth: 560,
                      width: "100%",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead
                      className="table-light sticky-top"
                      style={{ top: 0, zIndex: 2 }}
                    >
                      <tr>
                        <th
                          className="text-start"
                          style={{
                            width: 170,
                            minWidth: 170,
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            background: "#f9fafb",
                          }}
                        >
                          Nutrient
                        </th>
                        {matrixPhases.map((phase, idx) => {
                          const pId = String(phase.PhaseID || phase.id);
                          const hasDays =
                            phase.FromDays !== null &&
                            phase.ToDays !== null &&
                            phase.FromDays !== undefined &&
                            phase.ToDays !== undefined;
                          return (
                            <th
                              key={pId}
                              className="text-center"
                              style={{ minWidth: 90 }}
                            >
                              <div className="fw-semibold text-primary-light text-xs text-truncate">
                                {phase.PhaseName}
                              </div>
                              <div className="text-xs text-secondary-light mt-1">
                                {hasDays && `${phase.FromDays}-${phase.ToDays}d`}
                              </div>
                            </th>
                          );
                        })}
                        <th
                          className="text-center"
                          style={{
                            width: 90,
                            minWidth: 90,
                            background: "#f9fafb",
                          }}
                        >
                          <span className="text-xs">Total</span>
                          <div className="text-xs text-secondary-light mt-1 fw-normal">
                            kg/ha
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {NUTRIENT_COLUMNS.map((nutrient) => {
                        const rowTotal = calculateNutrientTotal(nutrient.key);
                        return (
                          <tr key={nutrient.key}>
                            <td
                              style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                background: "#fff",
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <span className="fw-medium text-primary-light text-xs text-truncate d-block">
                                  {nutrient.label}
                                </span>
                              </div>
                            </td>

                            {matrixPhases.map((phase) => {
                              const pId = String(phase.PhaseID || phase.id);
                              const cellVal =
                                matrixValues[nutrient.key]?.[pId] ?? "";
                              return (
                                <td
                                  key={`${nutrient.key}_${pId}`}
                                  className="text-center p-2"
                                >
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="form-control form-control-sm text-center fw-medium"
                                    placeholder="0"
                                    value={cellVal}
                                    onChange={(e) =>
                                      handleCellChange(
                                        nutrient.key,
                                        pId,
                                        e.target.value
                                      )
                                    }
                                    style={{
                                      backgroundColor:
                                        cellVal !== "" ? "#f0fdf4" : "#fff",
                                      borderColor:
                                        cellVal !== "" ? "#86efac" : undefined,
                                      maxWidth: 78,
                                      margin: "0 auto",
                                      padding: "4px 6px",
                                      fontSize: 13,
                                    }}
                                  />
                                </td>
                              );
                            })}

                            <td className="text-center fw-semibold text-primary-light bg-light">
                              {rowTotal !== "-" && rowTotal > 0 ? (
                                rowTotal
                              ) : (
                                <span className="text-secondary-light">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="pt-12 border-top d-flex justify-content-between flex-wrap gap-2">
                  {isEditMode && (
                    <button
                      type="button"
                      className="btn btn-light text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2"
                      onClick={() => navigate("/crop-nutrient")}
                    >
                      <Icon icon="lucide:arrow-left" style={{ fontSize: 16 }} />
                      <span>Back to List</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-yellow text-sm btn-sm px-24 py-12 radius-8 d-flex align-items-center gap-2 ms-auto"
                    onClick={handleSaveMatrix}
                    disabled={savingMatrix}
                  >
                    {savingMatrix ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Icon
                          icon="mdi:content-save"
                          style={{ fontSize: 16 }}
                        />
                        <span>Save Nutritive Values</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ================================================================ */}
        {/* EXCEL IMPORT / EXPORT */}
        {/* ================================================================ */}
        {activeTab === "excel" && (
          <div className="row g-4">
            {/* EXPORT */}
            <div className="col-lg-6">
              <div className="p-20 bg-light radius-12 h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-16">
                    <Icon
                      icon="mdi:file-download-outline"
                      style={{ fontSize: 22 }}
                      className="text-primary-600"
                    />
                    <div>
                      <h6 className="mb-0 fw-semibold text-primary-light">
                        Export Nutritive Values
                      </h6>
                      <p className="text-xs text-secondary-light mb-0">
                        Select crops & varieties to export as Excel.
                      </p>
                    </div>
                  </div>

                  {/* Crops */}
                  <div className="mb-16">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label text-xs fw-semibold text-primary-light mb-0">
                        Crops ({exportSelectedCrops.length}/{crops.length})
                      </label>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-xs text-primary-600 text-decoration-none"
                        onClick={handleSelectAllExportCrops}
                      >
                        {exportSelectedCrops.length === crops.length
                          ? "Deselect all"
                          : "Select all"}
                      </button>
                    </div>

                    <div className="navbar-search mb-2">
                      <input
                        className="bg-base h-36-px w-100"
                        placeholder="Filter crops..."
                        value={exportCropSearch}
                        onChange={(e) => setExportCropSearch(e.target.value)}
                      />
                      <Icon icon="ion:search-outline" className="icon" />
                    </div>

                    <div
                      className="bg-base border radius-8 p-8 overflow-auto"
                      style={{ maxHeight: 150 }}
                    >
                      {crops
                        .filter(
                          (c) =>
                            !exportCropSearch.trim() ||
                            c.CropName?.toLowerCase().includes(
                              exportCropSearch.toLowerCase()
                            )
                        )
                        .map((c) => {
                          const isChecked = exportSelectedCrops.includes(c.ID);
                          return (
                            <div
                              key={c.ID}
                              className="d-flex align-items-center gap-2 py-4 px-8 radius-4 cursor-pointer hover-bg-light"
                              onClick={() => handleToggleExportCrop(c.ID)}
                            >
                              <input
                                className="form-check-input mt-0 cursor-pointer"
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                              />
                              <span className="text-sm text-secondary-light">
                                {c.CropName}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Varieties */}
                  <div className="mb-16">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <label className="form-label text-xs fw-semibold text-primary-light mb-0">
                        Varieties ({exportSelectedVarieties.length}/
                        {allAvailableExportVarieties.length})
                      </label>
                      {allAvailableExportVarieties.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-xs text-primary-600 text-decoration-none"
                          onClick={handleSelectAllExportVarieties}
                        >
                          {exportSelectedVarieties.length ===
                          allAvailableExportVarieties.length
                            ? "Deselect all"
                            : "Select all"}
                        </button>
                      )}
                    </div>

                    {allAvailableExportVarieties.length > 0 && (
                      <div className="navbar-search mb-2">
                        <input
                          className="bg-base h-36-px w-100"
                          placeholder="Filter varieties..."
                          value={exportVarietySearch}
                          onChange={(e) => setExportVarietySearch(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                      </div>
                    )}

                    <div
                      className="bg-base border radius-8 p-8 overflow-auto"
                      style={{ maxHeight: 160 }}
                    >
                      {loadingExportVarieties ? (
                        <div className="text-center py-12 text-secondary-light text-xs">
                          Loading varieties...
                        </div>
                      ) : exportSelectedCrops.length === 0 ? (
                        <div className="text-center py-12 text-secondary-light text-xs">
                          Select a crop first
                        </div>
                      ) : allAvailableExportVarieties.length === 0 ? (
                        <div className="text-center py-12 text-secondary-light text-xs">
                          No varieties found
                        </div>
                      ) : (
                        allAvailableExportVarieties
                          .filter(
                            (v) =>
                              !exportVarietySearch.trim() ||
                              v.VariantName?.toLowerCase().includes(
                                exportVarietySearch.toLowerCase()
                              )
                          )
                          .map((v) => {
                            const isChecked =
                              exportSelectedVarieties.includes(v.ID);
                            return (
                              <div
                                key={v.ID}
                                className="d-flex align-items-center gap-2 py-4 px-8 radius-4 cursor-pointer hover-bg-light"
                                onClick={() => handleToggleExportVariety(v.ID)}
                              >
                                <input
                                  className="form-check-input mt-0 cursor-pointer"
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}}
                                />
                                <span className="text-sm text-secondary-light">
                                  {v.VariantName}
                                </span>
                              </div>
                            );
                          })
                      )}
                    </div>
                  </div>
                </div>

                <div className="pt-12 border-top">
                  <button
                    type="button"
                    className="btn btn-yellow w-100 text-sm py-10 radius-8 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleExportExcel}
                    disabled={
                      isExporting || exportSelectedVarieties.length === 0
                    }
                  >
                    {isExporting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        <span>Generating Excel...</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:file-excel" style={{ fontSize: 16 }} />
                        <span>
                          Export Excel ({exportSelectedVarieties.length}{" "}
                          varieties)
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* IMPORT */}
            <div className="col-lg-6">
              <div className="p-20 bg-light radius-12 h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-16">
                    <Icon
                      icon="mdi:cloud-upload-outline"
                      style={{ fontSize: 22 }}
                      className="text-success-600"
                    />
                    <div>
                      <h6 className="mb-0 fw-semibold text-primary-light">
                        Import Nutritive Values
                      </h6>
                      <p className="text-xs text-secondary-light mb-0">
                        Upload a filled spreadsheet to bulk-update nutrients.
                      </p>
                    </div>
                  </div>

                  <div className="bg-base p-12 radius-8 mb-16">
                    <div className="text-xs fw-semibold text-secondary-light text-uppercase mb-2">
                      Instructions
                    </div>
                    <ol className="text-xs text-secondary-light mb-0 ps-16 d-flex flex-column gap-1">
                      <li>Export the template using the left panel.</li>
                      <li>Fill decimal nutrient values for each phase.</li>
                      <li>
                        Save and upload below (<code>.xlsx</code> or{" "}
                        <code>.xls</code>).
                      </li>
                      <li>Existing records will be updated</li>
                    </ol>
                  </div>

                  <div
                    className="p-24 border border-dashed radius-12 text-center bg-base cursor-pointer hover-border-primary mb-16"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx, .xls"
                      className="d-none"
                      onChange={handleFileChange}
                    />

                    {importFile ? (
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon
                          icon="mdi:file-excel"
                          style={{ fontSize: 26 }}
                          className="text-success-600"
                        />
                        <div className="text-start">
                          <div className="fw-medium text-primary-light text-sm">
                            {importFile.name}
                          </div>
                          <div className="text-xs text-secondary-light">
                            {(importFile.size / 1024).toFixed(1)} KB · Ready
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-link text-danger-600 p-0 ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImportFile();
                          }}
                        >
                          <Icon icon="lucide:x" style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Icon
                          icon="lucide:upload-cloud"
                          style={{ fontSize: 28 }}
                          className="text-primary-600 mb-2"
                        />
                        <p className="text-sm fw-medium text-primary-light mb-1">
                          Click to browse or drag & drop Excel
                        </p>
                        <span className="text-xs text-secondary-light">
                          Accepts .xlsx or .xls
                        </span>
                      </>
                    )}
                  </div>

                  {importSuccessInfo && (
                    <div className="p-12 radius-8 bg-success-focus text-success-600 border border-success-main mb-16 d-flex align-items-start gap-2">
                      <Icon
                        icon="lucide:check-circle"
                        style={{ fontSize: 14 }}
                        className="mt-1 flex-shrink-0"
                      />
                      <div className="text-xs">
                        <div className="fw-semibold mb-1">Import Completed</div>
                        <div>
                          {importSuccessInfo.varieties_count !== undefined
                            ? `Updated ${importSuccessInfo.varieties_count} varieties with ${importSuccessInfo.records_count} phase records.`
                            : importSuccessInfo.message}
                        </div>
                      </div>
                    </div>
                  )}

                  {importErrors.length > 0 && (
                    <div
                      className="p-12 radius-8 bg-danger-focus text-danger-600 border border-danger-main mb-16 overflow-auto"
                      style={{ maxHeight: 180 }}
                    >
                      <div className="d-flex align-items-center gap-2 fw-semibold text-xs mb-2">
                        <Icon
                          icon="lucide:alert-circle"
                          style={{ fontSize: 14 }}
                        />
                        <span>Validation Errors ({importErrors.length})</span>
                      </div>
                      <ul className="text-xs mb-0 ps-16">
                        {importErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="pt-12 border-top">
                  <button
                    type="button"
                    className="btn btn-yellow w-100 text-sm py-10 radius-8 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleImportExcel}
                    disabled={isImporting || !importFile}
                  >
                    {isImporting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        <span>Validating & Importing...</span>
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:cloud-upload" style={{ fontSize: 16 }} />
                        <span>Upload & Import</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseWiseNutritiveValue;